import type React from 'react';
import { useContext, useEffect, useMemo } from 'react';
import { ConfigContext } from '../context/ConfigContext';
import { useChatStatus } from '../helpers/useChatStatus';
import { useTimer } from '../helpers/useTimer';
import analyticsService from '../services/analyticsService';
import type { BubbleProps } from '../types/Bubble';

const getIconByWindowType = (
  chatIcon: string | { open: string; closed: string },
  windowType: string,
  windowToggle: boolean,
) => {
  if (typeof chatIcon === 'string') {
    return chatIcon;
  }

  if (windowToggle && windowType === 'open') {
    return chatIcon.open;
  }
  return chatIcon.closed;
};

const Bubble: React.FC<BubbleProps> = ({
  clickHandler,
  windowType,
  windowToggle,
  changeChatActivityStatus,
}) => {
  const {
    bubbleProps: { dim, icon: chatIcon },
    configId,
  } = useContext(ConfigContext);

  const icon = useMemo(
    () => getIconByWindowType(chatIcon, windowType, windowToggle),
    [windowType, windowToggle, chatIcon],
  );
  const { setSendChatStatus } = useChatStatus();
  const { setWindowToggle, count } = useTimer(windowToggle);
  const isMobileDevice = analyticsService.isMobileDevice();

  // biome-ignore lint/correctness/useExhaustiveDependencies: changing dependencies might affect functionality
  useEffect(() => {
    if (count === 45) {
      setSendChatStatus({ status: 'idle' }, configId);
      changeChatActivityStatus('idle');
      setWindowToggle(false);
    }
  }, [count, setWindowToggle, configId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: changing dependencies might affect functionality
  useEffect(() => {
    const statusByWindowToggle = windowToggle ? 'online' : 'offline';
    setSendChatStatus({ status: statusByWindowToggle }, configId);
    changeChatActivityStatus(statusByWindowToggle);
    setWindowToggle(windowToggle);
  }, [windowToggle, configId]);

  return (
    <div
      className='chat-bubble-container'
      aria-hidden='true'
      onClick={(e) => {
        clickHandler(e);
      }}>
      {icon ? (
        <img
          className={`chat-bubble-icon ${isMobileDevice ? 'mobile' : ''}`}
          width={dim}
          height={dim}
          src={icon}
          alt='chat-bubble'
        />
      ) : (
        <i
          className={`far fa-comment chat-bubble-icon ${isMobileDevice ? 'mobile' : ''}`}
        />
      )}
    </div>
  );
};

export default Bubble;
